import React, { useState, useEffect, useCallback } from 'react';
import { attendanceAPI, usersAPI } from '../../services/api';
import { format, subDays } from 'date-fns';
import { Search, X, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

const FIELD_LABELS = {
  clock_in:        'Clock In',
  clock_out:       'Clock Out',
  shift_id:        'Shift',
  is_final:        'Finalized',
  is_overridden:   'Excused',
  override_remark: 'Excuse Remark',
};

const fmtVal = (key, val) => {
  if (val === null || val === undefined) return '—';
  if (key === 'clock_in' || key === 'clock_out') {
    const d = new Date(val);
    return isNaN(d) ? val : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
};

const DiffCell = ({ oldValues, newValues }) => {
  const diffs = Object.keys(newValues || {}).filter(
    k => JSON.stringify(oldValues?.[k]) !== JSON.stringify(newValues?.[k])
  );
  if (diffs.length === 0) return <span className="text-gray-400 italic text-xs">—</span>;
  return (
    <ul className="space-y-0.5 text-xs">
      {diffs.map(k => (
        <li key={k}>
          <span className="font-medium text-gray-600">{FIELD_LABELS[k] || k}:</span>{' '}
          <span className="text-red-500">{fmtVal(k, oldValues?.[k])}</span>
          {' → '}
          <span className="text-green-600">{fmtVal(k, newValues?.[k])}</span>
        </li>
      ))}
    </ul>
  );
};

const AttendanceChangeLog = () => {
  const today   = format(new Date(), 'yyyy-MM-dd');
  const weekAgo = format(subDays(new Date(), 7), 'yyyy-MM-dd');

  const [startDate, setStartDate]           = useState(weekAgo);
  const [endDate, setEndDate]               = useState(today);
  const [allUsers, setAllUsers]             = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  const [entries,  setEntries]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [pages,    setPages]    = useState(1);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const PER_PAGE = 50;

  useEffect(() => {
    usersAPI.getUsers({ per_page: 1000, show_inactive: false })
      .then(r => setAllUsers(r.data.users || []))
      .catch(() => {});
  }, []);

  const fetchHistory = useCallback(async (pg = 1) => {
    setLoading(true);
    setError('');
    try {
      const params = { start_date: startDate, end_date: endDate, page: pg, per_page: PER_PAGE };
      if (selectedUserId) params.user_id = selectedUserId;
      const res = await attendanceAPI.getChangeHistory(params);
      setEntries(res.data.history);
      setTotal(res.data.total);
      setPage(res.data.page);
      setPages(res.data.pages);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load change history');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedUserId]);

  useEffect(() => { fetchHistory(1); }, [fetchHistory]);

  const filteredUsers = allUsers.filter(u =>
    !userSearchTerm ||
    u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    u.staff_no.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white shadow rounded-lg px-4 py-4">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Employee search */}
          <div
            className="relative min-w-[220px]"
            onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setShowUserDropdown(false); }}
          >
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={userSearchTerm}
              onChange={e => { setUserSearchTerm(e.target.value); setShowUserDropdown(true); }}
              onFocus={() => setShowUserDropdown(true)}
              placeholder="All employees..."
              className="pl-9 pr-8 py-2 block w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            {(userSearchTerm || selectedUserId) && (
              <button
                className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                onMouseDown={e => { e.preventDefault(); setUserSearchTerm(''); setSelectedUserId(''); setShowUserDropdown(false); }}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {showUserDropdown && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto text-sm">
                <li
                  className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-gray-500"
                  onMouseDown={() => { setSelectedUserId(''); setUserSearchTerm(''); setShowUserDropdown(false); }}
                >
                  All Employees
                </li>
                {filteredUsers.map(u => (
                  <li
                    key={u.id}
                    className="px-3 py-2 cursor-pointer hover:bg-blue-50 hover:text-blue-700"
                    onMouseDown={() => { setSelectedUserId(String(u.id)); setUserSearchTerm(`${u.name} (${u.staff_no})`); setShowUserDropdown(false); }}
                  >
                    {u.name} <span className="text-gray-400">({u.staff_no})</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Date range */}
          <div className="flex items-center space-x-2 bg-white rounded-md border border-gray-300 px-2 shadow-sm">
            <span className="text-sm text-gray-500 font-medium">From</span>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="border-0 focus:ring-0 sm:text-sm py-2 px-1 text-gray-900"
            />
          </div>
          <div className="flex items-center space-x-2 bg-white rounded-md border border-gray-300 px-2 shadow-sm">
            <span className="text-sm text-gray-500 font-medium">To</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={e => setEndDate(e.target.value)}
              className="border-0 focus:ring-0 sm:text-sm py-2 px-1 text-gray-900"
            />
          </div>

          <button
            onClick={() => fetchHistory(1)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </button>

          {!loading && (
            <span className="ml-auto text-sm text-gray-500">{total} change{total !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No changes recorded in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Changed At', 'Employee', 'Workday', 'Fields Changed', 'Reason', 'Changed By'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {entries.map(e => (
                  <tr key={e.id} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                      {new Date(e.updated_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{e.employee || '—'}</div>
                      <div className="text-gray-400 text-xs">{e.staff_no}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{e.workday || '—'}</td>
                    <td className="px-4 py-3">
                      <DiffCell oldValues={e.old_values} newValues={e.new_values} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs max-w-xs">{e.reason || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-xs">{e.updated_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border border-gray-200 rounded-lg shadow-sm">
          <p className="text-sm text-gray-700">
            Page <span className="font-medium">{page}</span> of <span className="font-medium">{pages}</span>
            {' '}· <span className="font-medium">{total}</span> total
          </p>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => fetchHistory(page - 1)}
              disabled={page === 1}
              className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => fetchHistory(page + 1)}
              disabled={page === pages}
              className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceChangeLog;
